// Thông báo cấp trên gửi (docs/KE_HOACH_GUI_THONG_BAO.md) — nhãn loại + khung đọc dùng chung: trang Thông báo, chuông,
// dải Tổng quan, "Xem trước" ở trang Gửi thông báo, hộp thư Admin NPP.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ExternalLink, Info, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'

export type AnnounceKind = 'warning' | 'promo' | 'info'

// Độ mờ phải nằm trong thang mặc định của Tailwind (5, 10, 15…): `bg-danger/12`, `bg-ok/8` không sinh ra CSS nào.
export const ANNOUNCE_KIND: Record<AnnounceKind, { label: string; badge: string; band: string; text: string; icon: typeof Info }> = {
  warning: { label: 'Cảnh báo', badge: 'bg-danger/10 text-danger', band: 'border-danger/30 bg-danger/5', text: 'text-danger', icon: AlertTriangle },
  promo: { label: 'Khuyến mãi', badge: 'bg-ok/10 text-ok', band: 'border-ok/30 bg-ok/5', text: 'text-ok', icon: Tag },
  info: { label: 'Thông báo', badge: 'bg-info/10 text-info', band: 'border-info/30 bg-info/5', text: 'text-info', icon: Info },
}

export function announceKind(k: unknown): AnnounceKind {
  return k === 'warning' || k === 'promo' || k === 'info' ? k : 'info'
}

export function KindBadge({ kind, className }: { kind: AnnounceKind; className?: string }) {
  const k = ANNOUNCE_KIND[kind]
  return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', k.badge, className)}>{k.label}</span>
}

/** Link kèm thông báo: trang trong app → điều hướng; https → tab mới; dạng khác (chưa qua máy chủ kiểm) → không hiện. */
export function AnnouncementLink({
  url,
  className,
  children = 'Mở',
  onClick,
}: {
  url: string
  className?: string
  children?: ReactNode
  /** Khung đọc nằm trong khung trang (chuông) — đóng khung khi đi trang khác. */
  onClick?: () => void
}) {
  const cls = cn('motion-state inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-accent px-4 text-sm font-semibold text-onAccent hover:bg-accentSoft desktop:min-h-10', className)
  if (url.startsWith('/') && !url.startsWith('//')) return <Link to={url} onClick={onClick} className={cls}>{children}</Link>
  if (!/^https:\/\//i.test(url)) return null
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={onClick} className={cls}>
      {children}
      <ExternalLink className="size-3.5" aria-hidden />
    </a>
  )
}

export interface AnnouncementContent {
  kind: AnnounceKind
  title: string
  message: string
  sender?: string | null
  createdAt?: string | Date | null
  url?: string | null
}

/** Dòng thông báo (trang Thông báo, chuông, hộp thư Admin NPP: `data` = { kind, sender, url? }) → nội dung khung đọc. */
export function toAnnouncement(n: { title?: string | null; message?: string | null; data?: Record<string, unknown> | null; createdAt?: string | Date | null }): AnnouncementContent {
  const d = n.data ?? {}
  return {
    kind: announceKind(d.kind),
    title: n.title ?? '',
    message: n.message ?? '',
    sender: typeof d.sender === 'string' ? d.sender : null,
    createdAt: n.createdAt ?? null,
    url: typeof d.url === 'string' ? d.url : null,
  }
}

/** Nội dung đầy đủ — đặt trong Sheet / Modal / khung xem trước. */
export function AnnouncementView({
  a,
  className,
  hideTitle = false,
  onLinkClick,
}: {
  a: AnnouncementContent
  className?: string
  /** Khung chứa đã hiện tiêu đề (tiêu đề Sheet) — không lặp lại. */
  hideTitle?: boolean
  onLinkClick?: () => void
}) {
  const when = a.createdAt ? new Date(a.createdAt) : null
  return (
    <article className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <KindBadge kind={a.kind} />
        {a.sender && <span className="font-medium text-inkSoft">{a.sender}</span>}
        {when && !Number.isNaN(when.getTime()) && (
          <span className="tabular-nums">{when.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
        )}
      </div>
      {!hideTitle && <h3 className="break-words text-base font-semibold text-ink">{a.title}</h3>}
      <p className="whitespace-pre-line break-words text-sm leading-relaxed text-inkSoft">{a.message}</p>
      {a.url && <AnnouncementLink url={a.url} onClick={onLinkClick} />}
    </article>
  )
}
